"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

import { signup, ProvidersApiError } from "@/lib/api/providers";

/**
 * Provider self-onboarding form (Section 2 Real Negotiation Engine).
 *
 * Captures: business / contact / email / mobile, trade type, service
 * area (one or more postcodes / regions), capacity per week, discount
 * commitment for Mio-routed users, licence number (text only, not
 * registry-validated at MVP).
 *
 * Submits to /api/providers/signup; on success the API dispatches an
 * email OTP and we route the provider to /providers/verify/{id}.
 */
export function ProviderSignupForm() {
  const router = useRouter();
  const [businessName, setBusinessName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [tradeType, setTradeType] = useState("");
  const [serviceAreaText, setServiceAreaText] = useState("");
  const [capacity, setCapacity] = useState<string>("");
  const [discountCommitment, setDiscountCommitment] = useState("");
  const [licenceNumber, setLicenceNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const serviceArea = serviceAreaText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (serviceArea.length === 0) {
        setError("Service area is required (postcodes or suburbs, comma-separated).");
        setSubmitting(false);
        return;
      }
      const res = await signup({
        business_name: businessName.trim(),
        contact_name: contactName.trim(),
        email: email.trim(),
        mobile: mobile.trim() || undefined,
        trade_type: tradeType.trim().toLowerCase(),
        service_area: serviceArea,
        capacity_per_week: capacity ? Number(capacity) : undefined,
        discount_commitment: discountCommitment.trim() || undefined,
        licence_number: licenceNumber.trim() || undefined,
        country_code: "AU",
      });
      router.push(`/providers/verify/${res.provider_id}`);
    } catch (e) {
      if (e instanceof ProvidersApiError) {
        setError(`Signup failed (${e.status}). Please review the form and retry.`);
      } else {
        setError(
          e instanceof Error ? e.message : "Signup failed. Try again in a moment.",
        );
      }
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-8">
      <Section title="About your business">
        <Field label="Business name" required>
          <input
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            required
            maxLength={255}
            className={inputCls}
          />
        </Field>
        <Field label="Your name" required>
          <input
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            required
            maxLength={255}
            className={inputCls}
          />
        </Field>
        <Field
          label="Email"
          required
          hint="We send you a verification code to this address. Verify within 10 minutes."
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            maxLength={320}
            className={inputCls}
          />
        </Field>
        <Field
          label="Mobile"
          hint="Captured for future SMS verification. SMS verification is not enabled at MVP, so leaving this blank is fine."
        >
          <input
            type="tel"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            maxLength={32}
            className={inputCls}
          />
        </Field>
      </Section>

      <Section title="What you do">
        <Field
          label="Trade or service type"
          required
          hint="e.g. plumber, electrician, accountant, IT services, B2B SaaS."
        >
          <input
            type="text"
            value={tradeType}
            onChange={(e) => setTradeType(e.target.value)}
            required
            maxLength={64}
            className={inputCls}
          />
        </Field>
        <Field
          label="Service area"
          required
          hint="Postcodes or suburbs you serve, comma-separated. e.g. 2000, 2010, Sydney CBD."
        >
          <input
            type="text"
            value={serviceAreaText}
            onChange={(e) => setServiceAreaText(e.target.value)}
            required
            className={inputCls}
          />
        </Field>
        <Field
          label="Capacity per week"
          hint="How many Octopusden-routed jobs can you take in a typical week?"
        >
          <input
            type="number"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            min={0}
            max={10000}
            className={`${inputCls} w-32`}
          />
        </Field>
      </Section>

      <Section title="Your offer to Octopusden users">
        <Field
          label="Discount commitment"
          hint="What can Octopusden-routed customers expect? e.g. 10% off labour, free callout, fixed call-out fee."
        >
          <textarea
            value={discountCommitment}
            onChange={(e) => setDiscountCommitment(e.target.value)}
            maxLength={500}
            rows={3}
            className={`${inputCls} resize-y`}
          />
        </Field>
        <Field
          label="Licence number"
          hint="Self-declared. Not registry-validated at MVP. Customers see this on your profile."
        >
          <input
            type="text"
            value={licenceNumber}
            onChange={(e) => setLicenceNumber(e.target.value)}
            maxLength={64}
            className={inputCls}
          />
        </Field>
      </Section>

      <p className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
        Your public profile starts as <strong>self-declared, not yet verified</strong>.
        After 5+ completed jobs at average rating 4.0 or higher, you earn a verified badge.
        Behavioural verification (response time, completion, ratings) replaces upfront paperwork.
      </p>

      <div className="flex items-center gap-4">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-foreground px-5 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {submitting ? "Sending verification code..." : "Sign up and verify email"}
        </button>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </div>
    </form>
  );
}

const inputCls =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-base outline-none focus:border-foreground";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="border-0 p-0">
      <legend className="mb-3 text-base font-semibold text-foreground">{title}</legend>
      <div className="flex flex-col gap-4">{children}</div>
    </fieldset>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-foreground">
        {label}
        {required ? <span className="ml-1 text-destructive">*</span> : null}
      </span>
      {children}
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </label>
  );
}
